import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Gift, Check, Download, Sparkles } from "lucide-react";
import { toast } from "sonner";

/**
 * Free Resources Library — anyone can browse, authenticated users can claim.
 * Claimed items land in /toolbox (the user's personal library).
 */
export default function FreeResources() {
  const [products, setProducts] = useState([]);
  const [owned, setOwned] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState({});
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    setLoading(true);
    api.get("/products?tier=free")
      .then((r) => setProducts(r.data || []))
      .catch(() => toast.error("Could not load free resources."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!user) { setOwned(new Set()); return; }
    api.get("/me/toolbox")
      .then((r) => setOwned(new Set((r.data?.items || []).map((p) => p.id))))
      .catch(() => {});
  }, [user]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return products;
    return products.filter((p) => `${p.name} ${p.description || ""}`.toLowerCase().includes(s));
  }, [products, q]);

  const claim = async (p) => {
    if (!user) return nav("/login");
    setBusy((b) => ({ ...b, [p.id]: true }));
    try {
      const { data } = await api.post(`/products/${p.id}/claim`);
      setOwned((o) => new Set(o).add(p.id));
      toast.success("Added to your toolbox!", {
        action: {
          label: "Open Toolbox",
          onClick: () => nav("/toolbox"),
        },
      });
      // if we got a direct download url, pop it open
      if (data?.download_url && data.download_url !== "#") {
        window.open(data.download_url, "_blank", "noreferrer");
      }
    } catch {
      toast.error("Could not claim this resource.");
    } finally {
      setBusy((b) => ({ ...b, [p.id]: false }));
    }
  };

  return (
    <div className="bg-brand-alabaster min-h-screen">
      <Navbar />
      <section className="py-16 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="uppercase tracking-[0.28em] text-xs text-brand-pink mb-3">Free Resources Library</div>
        <h1 className="font-display text-5xl md:text-6xl max-w-3xl leading-tight">
          Checklists, scripts & cheat sheets — <span className="italic text-brand-pink">on the house</span>.
        </h1>
        <p className="mt-6 text-neutral-600 max-w-2xl text-lg">
          Grab any resource below — it lands in your Toolbox and you can re-download anytime. No upsells, no credit card.
        </p>

        <div className="mt-10 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input
              data-testid="free-resources-search"
              placeholder="Search the library…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="rounded-full h-11 pl-11 border-neutral-200 bg-white"
            />
          </div>
          {user && (
            <Link to="/toolbox">
              <Button data-testid="free-resources-my-toolbox" variant="outline" className="rounded-full border-brand-pink text-brand-pink hover:bg-brand-pink/5 h-11 px-5">
                <Sparkles className="w-4 h-4 mr-1.5" /> My Toolbox
              </Button>
            </Link>
          )}
        </div>

        <div className="mt-8">
          {loading ? (
            <div className="text-neutral-500 py-16 text-center">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="text-neutral-500 py-16 text-center">Nothing matches. Try clearing the search.</div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map((p) => (
                <FreeCard
                  key={p.id}
                  product={p}
                  owned={owned.has(p.id)}
                  busy={!!busy[p.id]}
                  onClaim={() => claim(p)}
                />
              ))}
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}

function FreeCard({ product, owned, busy, onClaim }) {
  return (
    <article
      data-testid={`free-item-${product.id}`}
      className="rounded-3xl bg-white border border-neutral-100 p-6 hover:shadow-sm transition flex flex-col"
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider mb-3">
        <span className="px-2 py-0.5 rounded-full bg-brand-pink/10 text-brand-pink">Free</span>
        <span className="text-neutral-500">{product.category || "General"}</span>
      </div>
      <h3 className="font-display text-xl leading-tight mb-2">{product.name}</h3>
      <p className="text-sm text-neutral-600 mb-5 flex-1 line-clamp-3">{product.description}</p>
      {owned ? (
        <div className="flex items-center gap-2">
          {product.download_url && product.download_url !== "#" ? (
            <a
              href={product.download_url}
              target="_blank"
              rel="noreferrer"
              data-testid={`free-download-${product.id}`}
              className="inline-flex items-center justify-center h-9 px-4 rounded-full bg-brand-pink text-white text-xs hover:bg-brand-pink/90 transition"
            >
              <Download className="w-3.5 h-3.5 mr-1.5" /> Download
            </a>
          ) : (
            <span className="inline-flex items-center h-9 px-4 rounded-full bg-neutral-100 text-neutral-500 text-xs">
              <Check className="w-3.5 h-3.5 mr-1.5" /> In your toolbox
            </span>
          )}
        </div>
      ) : (
        <Button
          onClick={onClaim}
          disabled={busy}
          data-testid={`free-claim-${product.id}`}
          className="rounded-full bg-brand-pink hover:bg-brand-pink/90 text-white btn-pink-glow self-start"
        >
          <Gift className="w-3.5 h-3.5 mr-1.5" /> {busy ? "Adding…" : "Grab it free"}
        </Button>
      )}
    </article>
  );
}
